import { PayloadAction } from '@reduxjs/toolkit';

import { DesignData } from 'editor/src/store/design/types';
import getDesignKey, { getDesignKeyFromDesign } from 'editor/src/store/variants/helpers/getDesignKey';
import {
  getDesignsBySize,
  getSizeSpecificAreas,
  getVariationSize,
  printsArea,
  withSizeSpecificAreasFrom,
} from 'editor/src/store/variants/helpers/sizeSpecificAreas';
import { VariantsState } from 'editor/src/store/variants/types';

import reflectDesignData from 'editor/src/util/reflectDesignData';
import { ReflectContext } from 'editor/src/util/reflectDesignData/getReflectContext';

import { UNSELECTED_PAGE_COUNT } from './setPageCountReducer';

const setGroupDesignDataReducer = (
  state: VariantsState,
  action: PayloadAction<{
    groupKey: string;
    designData: DesignData | undefined;
    reflectContext: ReflectContext;
    // GAPI-47583 — the caller could not compute a design for this group because
    // a design template is missing: whatever designData the group holds is now
    // out of date. NOT set on a spread-count mismatch, where the group
    // legitimately keeps its own design (GAPI-39157).
    templatesMissing?: boolean;
    /** The size `designData` was made on. Other sizes keep their own artwork in the size-specific print areas. */
    sourceSize?: string;
    /** The group was linked again: each size takes the linked groups' artwork of its size before its own. */
    relinked?: boolean;
    /** The group a new group's design was copied from: a size without artwork of its own takes that group's. */
    copiedFrom?: string;
  }>,
) => {
  const { groupKey, designData, reflectContext, templatesMissing, sourceSize, relinked, copiedFrom } = action.payload;
  const group = state.variationGroups.find((group) => group.key === groupKey);
  if (group) {
    if (designData === undefined && templatesMissing) {
      group.variationsInfo.forEach((variationInfo) => {
        if (variationInfo.designData) {
          variationInfo.designDataStale = true;
        }
      });
      return;
    }

    const { selectedPageCount } = state;
    const designDataKey = designData ? getDesignKeyFromDesign(designData) : undefined;
    const sizeSpecificAreas = getSizeSpecificAreas(state.product);
    const linkedVariants = group.linked
      ? state.variationGroups.filter(({ key, linked }) => linked && key !== groupKey).flatMap((g) => g.variationsInfo)
      : [];
    const copiedVariants = state.variationGroups.find(({ key }) => key === copiedFrom)?.variationsInfo ?? [];
    // Read before the loop below replaces them: a size's variants differ only by print-area combination, so one that
    // has no design yet takes its size's artwork from another.
    const groupSizeDesigns = getDesignsBySize(sizeSpecificAreas.length ? group.variationsInfo : [], state.product);
    const linkedSizeDesigns = getDesignsBySize(sizeSpecificAreas.length ? linkedVariants : [], state.product);
    const copiedSizeDesigns = getDesignsBySize(sizeSpecificAreas.length ? copiedVariants : [], state.product);
    group.variationsInfo.forEach((variationInfo) => {
      const designKey = getDesignKey(variationInfo.variation.productUid, {
        ...variationInfo,
        pageCount: selectedPageCount === UNSELECTED_PAGE_COUNT ? undefined : selectedPageCount,
      });
      const ownDesign = variationInfo.designData;
      const forThisSize = (design: DesignData) => {
        const size = getVariationSize(variationInfo.variation, state.product)?.value;
        if (!sizeSpecificAreas.length || !size) {
          return design;
        }
        // The design the change was made on holds its size's artwork, in the areas it prints.
        const changedOnThisSize = size === sourceSize && !relinked && !!designData;
        if (changedOnThisSize && sizeSpecificAreas.every((area) => printsArea(designData, area))) {
          return design;
        }
        const own = [ownDesign, ...(groupSizeDesigns.get(size) ?? [])];
        const linked = linkedSizeDesigns.get(size) ?? [];
        const sizeDesigns = relinked
          ? [...linked, ...own]
          : [...own, ...linked, ...(copiedSizeDesigns.get(size) ?? [])];
        return withSizeSpecificAreasFrom(
          design,
          changedOnThisSize ? [designData, ...sizeDesigns] : sizeDesigns,
          sizeSpecificAreas,
          reflectContext,
        );
      };
      if (designKey === designDataKey) {
        variationInfo.designData = designData && forThisSize(designData);
        variationInfo.designDataStale = undefined;
      } else {
        const template = state.designTemplates[designKey];
        // GAPI-39157: Don't overwrite existing designData when the template for
        // THIS variant has a different spread count than the incoming designData.
        // This happens when "Apply changes to all variants" propagates a 2-spread
        // design (two-sided flyer) onto a 1-spread variant (one-sided flyer).
        // reflectDesignData would produce output matching the template's spread
        // count, but we skip it entirely because the variant already has correct
        // designData and the incoming design is structurally incompatible.
        // Guard only fires when all three exist: prior designData, template, AND
        // incoming designData — if any is missing, we still want reflection to run.
        const spreadCountMismatch =
          variationInfo.designData && template && designData && template.spreads.length !== designData.spreads.length;
        if (!spreadCountMismatch) {
          const reflected =
            designData && template ? reflectDesignData(designData, template, reflectContext) : undefined;
          if (reflected !== undefined) {
            variationInfo.designData = forThisSize(reflected);
            variationInfo.designDataStale = undefined;
          } else if (designData && !template) {
            // GAPI-47583: the design moved on but this variant could not follow
            // because its template has not been fetched yet
            // (`checkForMissingTemplatesOperation` requests it, and
            // `applyTemplatesIfNeededOperation` applies the current design once
            // it lands). Until then its designData is out of date — flag it so
            // `useVariantWatcher` does not present the snapshot to the host as
            // current. NOT flagged on `spreadCountMismatch`: there the variant
            // legitimately keeps its own design (GAPI-39157).
            variationInfo.designDataStale = true;
          }
        }
      }
    });
  }
};

export default setGroupDesignDataReducer;
